import { Alert, Button, Form, Modal } from "react-bootstrap";
import { ADMIN_LANG } from "../../../../settings";
import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/store";
import { closeModal, changeModal, getElement, saveElement } from "../../../../app/slices/modals/collectionElementModal";
import RichTextField from "../../RichTextField";
import ImageField from "../../ImageField";
import { v4 as uuidv4 } from "uuid";
import VideoField from "../../VideoField";
import AdminLoader from "../../AdminLoader";

const CollectionElementModal = () => {
    const dispatch = useAppDispatch();
    const {
        loading,
        isOpen,
        type,
        error,
        columnId,
        rowId,
        element_id,
        text,
        default_image,
        language_image,
        default_video,
        language_video,
        default_vimeo,
        language_vimeo,
        video_autoplay,
        video_loop,
        isSaving,
    } = useAppSelector((state) => state.collectionElementModal);
    const { languageTab, slide_id } = useAppSelector((state) => state.collectionEditModal);

    const [defaultImage, setDefaultImage] = React.useState<File | null>(null);
    const [image, setImage] = React.useState<File | null>(null);
    const textRef = React.useRef(text);

    const [defaultVideo, setDefaultVideo] = React.useState<File | null>(null);
    const [video, setVideo] = React.useState<File | null>(null);

    useEffect(() => {
        if (element_id && slide_id) {
            dispatch(getElement({ element_id, slide_id, language: languageTab }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element_id]);

    useEffect(() => {
        textRef.current = text || '';
    }, [text]);

    const handleClose = () => {
        dispatch(closeModal());
    };

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('content_id', slide_id || '');
        formData.append('type', type);
        formData.append('row_id', rowId);
        formData.append('column_id', columnId);
        formData.append('language', languageTab);
        formData.append('text', textRef.current);

        if (element_id) {
            formData.append('element_id', element_id);
        }

        if (defaultImage) {
            formData.append('default_image', defaultImage, uuidv4() + defaultImage.name);
        } else if (default_image) {
            formData.append('default_image', default_image.name);
        }
        if (image) {
            formData.append('language_image', image, uuidv4() + image.name);
        } else if (language_image) {
            formData.append('language_image', language_image.name);
        }
        if (defaultVideo) {
            formData.append('default_video', defaultVideo, uuidv4() + defaultVideo.name);
        } else if (default_video) {
            formData.append('default_video', default_video.url);
        }
        if (video) {
            formData.append('language_video', video, uuidv4() + video.name);
        } else if (language_video) {
            formData.append('language_video', language_video.url);
        }

        if (video_autoplay) {
            formData.append('video_autoplay', 'true');
        }

        if (video_loop) {
            formData.append('video_loop', 'true');
        }

        if (type === 'vimeo') {
            formData.append('default_vimeo', default_vimeo || '');
            formData.append('language_vimeo', language_vimeo || '');
        }

        dispatch(saveElement(formData));
    };

    const handleTextChange = (text: string) => {
        textRef.current = text;
        dispatch(changeModal({ text }));
    };

    const handleAutoplayChange = (video_autoplay: boolean) => {
        dispatch(changeModal({ video_autoplay }));
    };

    const handleLoopChange = (video_loop: boolean) => {
        dispatch(changeModal({ video_loop }));
    };

    const handleDefaultVideoChange = (video: File | null) => {
        if (video) {
            setDefaultVideo(video);
        } else {
            setDefaultVideo(null);
            dispatch(changeModal({ default_video: null }));
        }
    };

    const handleVideoChange = (video: File | null) => {
        if (video) {
            setVideo(video);
        } else {
            setVideo(null);
            dispatch(changeModal({ language_video: null }));
        }
    };

    const handleDefaultImageChange = (image: File | null) => {
        if (image) {
            setDefaultImage(image);
        } else {
            setDefaultImage(null);
            dispatch(changeModal({ default_image: null }));
        }
    };

    const handleImageChange = (image: File | null) => {
        if (image) {
            setImage(image);
        } else {
            setImage(null);
            dispatch(changeModal({ language_image: null }));
        }
    };

    const handleDefaultVimeoChange = (value: string) => {
        dispatch(changeModal({ default_vimeo: value }));
    };

    const handleLanguageVimeoChange = (value: string) => {
        dispatch(changeModal({ language_vimeo: value }));
    };

    let elem;
    if (type === 'text') {
        elem = (
            <Form.Group controlId={`text`}>
                <Form.Label>{ADMIN_LANG.text} ({languageTab.toUpperCase()})</Form.Label>
                <RichTextField value={text} onChange={handleTextChange} />
            </Form.Group>
        );
    } else if (type === 'image') {
        elem = (
            <div>
                <ImageField
                    defaultImage={default_image}
                    setImage={handleDefaultImageChange}
                    label='Oletuskuva'
                    imageWidth='350px'
                />
                <ImageField
                    defaultImage={language_image}
                    setImage={handleImageChange}
                    label='Kielikohtainen kuva'
                    imageWidth='350px'
                />
            </div>
        );
    } else if (type === 'video') {
        elem = (
            <div>
                <Form.Group className='pb-3'>
                    <Form.Switch
                        label={ADMIN_LANG.autoplay}
                        id={`autoplay`}
                        className='form-label'
                        checked={video_autoplay}
                        onChange={(e) => handleAutoplayChange(e.target.checked)}
                    />
                    <Form.Switch
                        label={ADMIN_LANG.loop_video}
                        id={`loop`}
                        className='form-label'
                        checked={video_loop}
                        onChange={(e) => handleLoopChange(e.target.checked)}
                    />
                </Form.Group>
                <VideoField
                    defaultVideo={default_video}
                    setVideo={handleDefaultVideoChange}
                    label='Oletusvideo'
                    previewWidth='350px'
                />
                <VideoField
                    defaultVideo={language_video}
                    setVideo={handleVideoChange}
                    label='Kielikohtainen video'
                    previewWidth='350px'
                />
            </div>
        );
    } else if (type === 'vimeo') {
        elem = (
            <div>
                <Form.Group className='pb-3' controlId='default-vimeo'>
                    <Form.Label>Vimeo embed (oletus)</Form.Label>
                    <Form.Control
                        as='textarea'
                        rows={3}
                        value={default_vimeo || ''}
                        onChange={(e) => handleDefaultVimeoChange(e.target.value)}
                        placeholder='https://player.vimeo.com/video/123456789'
                    />
                    <Form.Text>
                        Kopioi Vimeo-videon Share &gt; Embed -näkymästä iframe tai vain osoite, ja liitä se tähän.
                    </Form.Text>
                </Form.Group>
                <Form.Group controlId='language-vimeo'>
                    <Form.Label>Vimeo embed ({languageTab.toUpperCase()})</Form.Label>
                    <Form.Control
                        as='textarea'
                        rows={3}
                        value={language_vimeo || ''}
                        onChange={(e) => handleLanguageVimeoChange(e.target.value)}
                        placeholder='https://player.vimeo.com/video/123456789'
                    />
                    <Form.Text>
                        Jätä jos haluat käyttää oletuslinkkiä tämän kielen tilalla.
                    </Form.Text>
                </Form.Group>
            </div>
        );
    }

    const modalTitle = element_id ? ADMIN_LANG.edit_element : ADMIN_LANG.add_element;

    return (
        <Modal backdropClassName="nested-backdrop" className='admin-modal' show={isOpen} onHide={handleClose}>
            {isSaving && <AdminLoader />}
            <Modal.Header closeButton>
                <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" dismissible>
                        <div dangerouslySetInnerHTML={{ __html: error }} />
                    </Alert>
                )}
                <Form id='collection-element-modal-form' onSubmit={handleSave}>
                    {!loading && elem}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant='outline-primary' onClick={handleClose}>
                    {ADMIN_LANG.close}
                </Button>
                <Button variant='primary' type='submit' form='collection-element-modal-form'>
                    {!element_id ? ADMIN_LANG.add : ADMIN_LANG.save}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CollectionElementModal;
